import type { LoginDTO, PrincipalDTO, TokensDTO } from '@serviquim/contracts';
import { type SessionRecord, db } from './db';
import { ApiError, type HttpRequestOptions, httpRequest } from './http';

/** Sessão expirada e o refresh também falhou — só resta pedir login de novo. */
export class AuthExpiredError extends Error {
  constructor() {
    super('Sessão expirada — faça login novamente');
    this.name = 'AuthExpiredError';
  }
}

type Listener = () => void;

const listeners = new Set<Listener>();
let current: SessionRecord | undefined;
let ready = false;
let readyPromise: Promise<void> | undefined;

function notify(): void {
  for (const listener of listeners) listener();
}

/** Assina mudanças de sessão (login/logout/refresh) — usado pelo hook `useAuth`. */
export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSession(): SessionRecord | undefined {
  return current;
}

export function isSessionReady(): boolean {
  return ready;
}

/** Carrega a sessão persistida no Dexie ao iniciar o app (uma vez). */
export function initSession(): Promise<void> {
  if (!readyPromise) {
    readyPromise = db.session.get(1).then((record) => {
      current = record;
      ready = true;
      notify();
    });
  }
  return readyPromise;
}

async function persist(record: SessionRecord): Promise<void> {
  current = record;
  await db.session.put(record);
  notify();
}

export async function login(tenantSlug: string, email: string, senha: string): Promise<void> {
  const dto: LoginDTO = { tenantSlug, email, senha };
  const tokens = await httpRequest<TokensDTO>('/auth/login', { method: 'POST', body: dto });
  const principal = await httpRequest<PrincipalDTO>('/auth/me', {
    headers: { authorization: `Bearer ${tokens.accessToken}` },
  });
  await persist({
    id: 1,
    tenantSlug,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    principal,
  });
}

export async function logout(): Promise<void> {
  const session = current;
  current = undefined;
  await db.session.delete(1);
  notify();
  if (session) {
    // Best-effort: revoga o refresh token no servidor, mas o logout local
    // não pode ficar preso esperando rede (o viajante pode estar offline).
    void httpRequest('/auth/logout', {
      method: 'POST',
      body: { refreshToken: session.refreshToken },
    }).catch(() => undefined);
  }
}

async function refreshSession(): Promise<SessionRecord> {
  if (!current) throw new AuthExpiredError();
  const tokens = await httpRequest<TokensDTO>('/auth/refresh', {
    method: 'POST',
    body: { refreshToken: current.refreshToken },
  });
  const updated: SessionRecord = {
    ...current,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
  };
  await persist(updated);
  return updated;
}

type AuthorizedRequestOptions = Omit<HttpRequestOptions, 'headers'>;

/**
 * Requisição autenticada: injeta o Bearer token; se o servidor responder 401,
 * tenta renovar via refresh token UMA vez e refaz a chamada original. Se o
 * refresh também falhar, encerra a sessão local e propaga `AuthExpiredError`.
 */
export async function authorizedRequest<T>(
  path: string,
  options: AuthorizedRequestOptions = {},
): Promise<T> {
  if (!current) throw new AuthExpiredError();
  try {
    return await httpRequest<T>(path, {
      ...options,
      headers: { authorization: `Bearer ${current.accessToken}` },
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const refreshed = await refreshSession().catch(async () => {
        await logout();
        throw new AuthExpiredError();
      });
      return httpRequest<T>(path, {
        ...options,
        headers: { authorization: `Bearer ${refreshed.accessToken}` },
      });
    }
    throw error;
  }
}
